import { useEffect, useRef, useState } from 'react';
import {
  createAssignment,
  createCourseResource,
  createExam,
  createLesson,
  updateAssignment,
  updateExam,
  updateLesson,
  uploadCourseResource,
} from '@/lib/api';
import { kindLabel } from '@/lib/courseRoom';
import CourseResources from '@/components/course/CourseResources';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Link2, Loader2, Paperclip } from 'lucide-react';

const GRADE_CATEGORIES = ['Assignment', 'Quiz', 'Exam', 'Project', 'Participation'];

const ATTACH_ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.zip', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.mp3', '.m4a',
].join(',');

const EMPTY = {
  title: '',
  body_html: '',
  module_id: '',
  status: 'published',
  grade_category: 'Assignment',
  total_marks: 100,
  due_date: '',
  lock_after_due: false,
  duration_minutes: 60,
};

/**
 * One labelled form field.
 *
 * `htmlFor` ties the visible text to the control, so assistive technology reads
 * a name rather than an unlabelled box, and `error` renders beside the field —
 * a toast that vanishes after two seconds is not an error message a person can
 * act on, and never marks the offending field.
 */
function Field({ label, hint, htmlFor, error, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error && (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} role="alert" className="mt-1 text-xs font-medium text-rose-600">
          {error}
        </p>
      )}
      {hint && <p id={htmlFor ? `${htmlFor}-hint` : undefined} className="mt-1 text-xs text-[#062B1A]/40">{hint}</p>}
    </div>
  );
}

/**
 * Create or edit one piece of classwork.
 *
 * The three kinds share almost everything — a title, rich instructions, the
 * module they sit under, when they go live, and the files and links students
 * work from — so they share one dialog. Only the marking fields differ.
 *
 * Attachments hang off the saved item, so on create the dialog saves first and
 * then reopens on the attachment step rather than holding files in memory and
 * risking losing them.
 */
export default function ClassworkDialog({ open, onOpenChange, courseId, kind, item, modules, onSaved }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Which fields failed validation, so the message can sit on the field.
  const [errors, setErrors] = useState({});
  const [savedId, setSavedId] = useState('');
  const [linkDraft, setLinkDraft] = useState('');
  const [uploadPercent, setUploadPercent] = useState(null);

  const isEdit = !!item;
  const activeKind = kind || item?.kind || 'material';
  const ownerId = savedId || item?.id || '';

  useEffect(() => {
    if (!open) return;
    setSavedId('');
    setLinkDraft('');
    setForm(item ? {
      ...EMPTY,
      title: item.title || '',
      body_html: item.body_html || (item.content || item.description || ''),
      module_id: item.module_id || '',
      status: item.status === 'draft' ? 'draft' : 'published',
      grade_category: item.grade_category || 'Assignment',
      total_marks: item.total_marks ?? 100,
      due_date: item.due_date || '',
      lock_after_due: !!item.lock_after_due,
      duration_minutes: item.duration_minutes ?? 60,
    } : { ...EMPTY, grade_category: activeKind === 'quiz' ? 'Quiz' : 'Assignment' });
  }, [open, item, activeKind]);

  const save = async () => {
    if (!form.title.trim()) {
      setErrors({ title: 'Give it a title.' });
      toast.error('Give it a title');
      return null;
    }
    setErrors({});
    const common = {
      title: form.title.trim(),
      body_html: form.body_html,
      module_id: form.module_id,
      status: form.status,
    };
    try {
      setSaving(true);
      let saved;
      if (activeKind === 'material') {
        const payload = { ...common, content: '', type: 'text', video_type: 'none', duration_minutes: 0 };
        saved = isEdit
          ? await updateLesson(item.id, payload)
          : await createLesson({ ...payload, course_id: courseId, order: 0 });
      } else if (activeKind === 'assignment') {
        const payload = {
          ...common,
          description: '',
          instructions: '',
          total_marks: Number(form.total_marks) || 0,
          due_date: form.due_date,
          grade_category: form.grade_category,
          lock_after_due: form.lock_after_due,
        };
        saved = isEdit
          ? await updateAssignment(item.id, payload)
          : await createAssignment({ ...payload, course_id: courseId });
      } else {
        const payload = {
          ...common,
          description: '',
          total_marks: Number(form.total_marks) || 0,
          due_date: form.due_date,
          duration_minutes: Number(form.duration_minutes) || 60,
        };
        saved = isEdit
          ? await updateExam(item.id, payload)
          : await createExam({ ...payload, course_id: courseId, questions: [] });
      }
      return saved?.data || null;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not save this ${kindLabel(activeKind).toLowerCase()}`);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndClose = async () => {
    const saved = await save();
    if (!saved) return;
    toast.success(`${kindLabel(activeKind)} ${isEdit ? 'updated' : 'created'}`);
    onOpenChange(false);
    onSaved?.();
  };

  /** Make sure there is a saved item to hang an attachment on. */
  const ensureOwner = async () => {
    if (ownerId) return ownerId;
    const saved = await save();
    if (!saved?.id) return '';
    setSavedId(saved.id);
    onSaved?.();
    return saved.id;
  };

  const handleAttach = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const owner = await ensureOwner();
    if (!owner) return;
    try {
      setUploadPercent(0);
      await uploadCourseResource(courseId, file, { itemId: owner, onProgress: setUploadPercent });
      toast.success(`${file.name} attached`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadPercent(null);
    }
  };

  const handleAddLink = async () => {
    const url = linkDraft.trim();
    if (!url) return;
    const owner = await ensureOwner();
    if (!owner) return;
    try {
      await createCourseResource(courseId, { kind: 'link', url, item_id: owner, status: 'published' });
      setLinkDraft('');
      toast.success('Link attached');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not attach that link');
    }
  };

  const graded = activeKind !== 'material';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
            {isEdit ? 'Edit' : 'Create'} {kindLabel(activeKind)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Title" htmlFor="classwork-title" error={errors.title}>
            <Input
              id="classwork-title"
              value={form.title}
              onChange={(e) => {
                setForm((p) => ({ ...p, title: e.target.value }));
                if (errors.title) setErrors((p) => ({ ...p, title: '' }));
              }}
              placeholder="Enter title"
              aria-invalid={errors.title ? 'true' : undefined}
              aria-describedby={errors.title ? 'classwork-title-error' : undefined}
              className={errors.title ? 'border-rose-400 focus-visible:ring-rose-400' : undefined}
            />
          </Field>

          <Field label="Instructions / Description">
            <RichTextEditor
              aria-label="Instructions and description"
              value={form.body_html}
              onChange={({ html }) => setForm((p) => ({ ...p, body_html: html }))}
            />
          </Field>

          <Field label="Module (optional)" htmlFor="classwork-module">
            <Select
              value={form.module_id || 'none'}
              onValueChange={(v) => setForm((p) => ({ ...p, module_id: v === 'none' ? '' : v }))}
            >
              <SelectTrigger id="classwork-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No module</SelectItem>
                {(modules || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {graded && (
            <>
              {activeKind === 'assignment' && (
                <Field label="Grade Category" htmlFor="classwork-category" hint="Determines how this item is weighted in the final grade.">
                  <Select value={form.grade_category} onValueChange={(v) => setForm((p) => ({ ...p, grade_category: v }))}>
                    <SelectTrigger id="classwork-category" aria-describedby="classwork-category-hint"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GRADE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Points" htmlFor="classwork-points">
                  <Input
                    id="classwork-points"
                    type="number"
                    value={form.total_marks}
                    onChange={(e) => setForm((p) => ({ ...p, total_marks: e.target.value }))}
                  />
                </Field>
                <Field label="Due Date" htmlFor="classwork-due">
                  <Input
                    id="classwork-due"
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))}
                  />
                </Field>
              </div>

              {activeKind === 'quiz' && (
                <Field label="Time limit (minutes)" htmlFor="classwork-duration">
                  <Input
                    id="classwork-duration"
                    type="number"
                    value={form.duration_minutes}
                    onChange={(e) => setForm((p) => ({ ...p, duration_minutes: e.target.value }))}
                  />
                </Field>
              )}

              {activeKind === 'assignment' && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-[rgba(6,43,26,0.12)] p-3">
                  <div>
                    <p className="text-sm font-medium text-[#062B1A]">Lock submissions after deadline</p>
                    <p className="text-xs text-[#062B1A]/45">Students won't be able to submit once the due date passes.</p>
                  </div>
                  <Switch
                    checked={form.lock_after_due}
                    onCheckedChange={(v) => setForm((p) => ({ ...p, lock_after_due: !!v }))}
                    aria-label="Lock submissions after deadline"
                  />
                </div>
              )}
            </>
          )}

          <Field label="Publish">
            <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="published">Publish immediately</SelectItem>
                <SelectItem value="draft">Save as draft</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Attachments" hint="PDF, DOCX, XLSX, PPTX, images, CSV, TXT · Max 50MB each">
            <input ref={fileRef} type="file" accept={ATTACH_ACCEPT} className="hidden" aria-label="Attach files to this item" onChange={handleAttach} data-testid="classwork-file-input" />
            <Button
              type="button"
              variant="outline"
              className="w-full border-dashed"
              disabled={uploadPercent !== null || saving}
              onClick={() => fileRef.current?.click()}
            >
              <Paperclip className="mr-2 size-4" />Attach Files
            </Button>
            {uploadPercent !== null && (
              <div className="mt-2 space-y-1">
                <Progress value={uploadPercent} className="h-2" />
                <p className="text-xs text-[#062B1A]/45">Uploading… {uploadPercent}%</p>
              </div>
            )}
          </Field>

          <Field label="Links" htmlFor="classwork-link" hint="Press Enter or click Add to attach a link.">
            <div className="flex gap-2">
              <Input
                id="classwork-link"
                value={linkDraft}
                onChange={(e) => setLinkDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLink(); } }}
                placeholder="https://example.com/resource"
                aria-describedby="classwork-link-hint"
              />
              <Button type="button" variant="outline" onClick={handleAddLink}>
                <Link2 className="mr-1.5 size-4" />Add
              </Button>
            </div>
          </Field>

          {ownerId && (
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3">
              <CourseResources courseId={courseId} itemId={ownerId} editable compact emptyLabel="Nothing attached yet." />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#0F9D58] text-white hover:bg-[#0B7C45]" disabled={saving} onClick={handleSaveAndClose}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
